import { Autocomplete, Button, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import ThemeTable from "./ThemeTable/ThemeTable";
import { useContext, useEffect, useState } from "react";
import { DesignThemeEndPointLogic } from "../../../../logic/AssetManagement/Design/Theme/DesignThemeEndPointLogic";
import { useSelector } from "react-redux";
import {selectUserData,selectUserToken} from "../../../../features/userSlice";
import {setSelectedSharedUid,setSelectedSiteURL,setSelectedSite,setSiteOptions,setSelectedWebsiteName,setDesignThemesData,setDesignTemplatesDataTheme,setSelectedFeatures,setSelectedGroups,setSelectedTheme,
} from "../../../../features/themeSlice";
import { useDispatch } from "react-redux";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import { DesignTemplatesEndPointLogic } from "../../../../logic/AssetManagement/Design/Templates/DesignTemplatesEndPointLogic";
import { useLocation, useSearchParams } from "react-router-dom";
import store from "../../../../store";
import SharedStateContext from "../../../SharedStateProvider";

function Theme() {
  const tableHeadings = {
    name: "Name",
    groups_appearance: "Related Groups",
    modified: "Last Updated",
    Features: "Features",
    quick_actions: "Quick Actions",
    more_info: "More Info",
  };
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const response = useSelector(selectUserData);
  const { siteOptions, selectedSharedUid } = useSelector(
    (state: any) => state?.theme
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [dataShow, setDataShow] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [search, setSearch] = useState(false);
  const { shouldRunEffect, setLoad } = useContext(SharedStateContext);
  const [tableRefresh, setTableRefresh] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  //-- Get Websites list(name and shared_uid) and store it in the store
  useEffect(() => {
    if (response?.data?.website_permissions) {
      const siteOptions = response.data.website_permissions.map(
        (siteObj: any) => {
          const uid = Object.keys(siteObj)[0]; // UID of the site
          const siteName = siteObj[uid].ui_site_name; // Site name
          return { uid, siteName }; // Return as an object with uid and siteName
        }
      );
      store.dispatch(setSiteOptions(siteOptions)); // Dispatch the memoized options
    }
    // eslint-disable-next-line
  }, [response]); // Runs when `response` changes

  useEffect(() => {
    if (!selectedSharedUid) {
      setSearch(false);
      setDataShow(false);
    }
    setLoad(false);
    // eslint-disable-next-line
  }, [shouldRunEffect, selectedSharedUid, setLoad]);

  useEffect(() => {
    const siteFromURL = searchParams.get("selectedSite");
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("themeSharedUid"); // Remove themeSharedUid from URL
    setSearchParams(updatedParams, { replace: true });
    if (siteFromURL) {
      const isValidSite = siteOptions?.find(
        (site: any) => site.uid === siteFromURL
      );
      if (isValidSite) {
        store.dispatch(setSelectedSharedUid(siteFromURL)); // Set the selected site
        setSearch(true);
        setDataShow(true);
      } else {
        // Redirect to home if invalid site
        updatedParams.delete("selectedSite");
      }
    }
    setSearchParams(updatedParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteOptions])

  //-- this function is for set and delete the site id of the Current URL Path
  const updateURLParams = (siteUid: any) => {
    const updatedParams = new URLSearchParams(window.location.search); // Get search query parameters from current URL Path

    if (siteUid) {
      updatedParams.set("selectedSite", siteUid); // Update the query parameter value
    } else {
      updatedParams.delete("selectedSite"); // Remove query parameter if null
    }

    setSearchParams(updatedParams, { replace: true }); // update the URL's query parameters without reloading the page
  };

  //--Function to clear filters and clear query parameters
  const clearFilters = () => {
    setSearchParams({}); // clear all URL's query parameters
    dispatch(setSelectedSite(null));
    dispatch(setSelectedFeatures(null));
    dispatch(setSelectedGroups(null));
    dispatch(setSelectedTheme(null));
    dispatch(setSelectedWebsiteName(null));
    dispatch(setDesignThemesData(null));
    dispatch(setDesignTemplatesDataTheme(null));
  };

  //-- OnChange of Autocomplete Search Website
  const handleSelectWebsite = (_: unknown, newValue: any) => {
    const params = new URLSearchParams(location.search);
    if (!newValue) {
      clearFilters(); //when onChange clear all filters and clear query parameters
      dispatch(setSelectedSharedUid(null));
      dispatch(setSelectedWebsiteName(null));
      dispatch(setDesignThemesData(null));
      dispatch(setDesignTemplatesDataTheme(null));
      dispatch(setSelectedSiteURL(null));
      updateURLParams(null); // Remove selected website shared ID from URL
    } else {
      // Clear filters but keep the selected site information
      dispatch(setSelectedSharedUid(newValue.uid));
      dispatch(setSelectedSiteURL(newValue.url));
      dispatch(setSelectedWebsiteName(newValue.siteName));
      setSearch(false);
      setDataShow(false);
      dispatch(setSelectedFeatures(null));
      dispatch(setSelectedGroups(null));
      dispatch(setSelectedTheme(null));
      dispatch(setDesignThemesData(null));
      dispatch(setDesignTemplatesDataTheme(null));
      params.delete("search");
      params.delete("filters");
      setSearchParams(params);
    }
  };

  //-- Clicking on Select Site Button and make API call
  const handleSearch = async () => {
    if (selectedSharedUid) {
      setSearch(true);
      setDataShow(true);
      store.dispatch(setSelectedSite(selectedSharedUid));
      // Update the URL with selected site's UID
      updateURLParams(selectedSharedUid);
    }
  };

  //-- Set / Delete the selected site(URL's query parameter) According to URL selectedSite id (shared UID)
  useEffect(() => {
    const siteFromURL = searchParams.get("selectedSite"); // Get query parameter from URL
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("themeSharedUid"); // Remove themeSharedUid from URL

    setSearchParams(updatedParams, { replace: true }); // Update URL query parameter without reloading

    if (
      siteFromURL &&
      siteOptions?.find((site: any) => site.uid === siteFromURL)
    ) {
      dispatch(setSelectedSharedUid(siteFromURL)); // Set the selected site
    }
    // eslint-disable-next-line
  }, [siteOptions]);

  useEffect(() => {
    setDataLoaded(false);
    setTableRefresh(false);
    if (!search || !dataShow || !selectedSharedUid) return;
    setLoading(true);
    const fetchTemplates = async () => {
      try {
        await DesignThemeEndPointLogic.getDesignThemesData(oauthTokenHeader, selectedSharedUid, dispatch);
        await DesignTemplatesEndPointLogic.getDesignTemplatesTheme(oauthTokenHeader, selectedSharedUid, dispatch);
      } catch (error: any) {
        console.error("Error fetching design templates:", error);
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    };
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataShow, selectedSharedUid, tableRefresh])

  useEffect(() => {
    const hasSelectedSite = searchParams.has("selectedSite");
    if (!hasSelectedSite && selectedSharedUid !== null) {
      dispatch(setSelectedSharedUid(null));
      dispatch(setSelectedSite(null));
      setDataShow(false);
      setSearch(false);
    }
    // Run this effect only when the searchParams or siteOptions change
    // eslint-disable-next-line
  }, [searchParams, dispatch]);

  return (
    <>
      <Paper sx={{ padding: "8px 16px" }}>
        <Typography fontSize={24}>Themes</Typography>
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          gap={4}
          mt={2}
        >
          <Autocomplete
            disablePortal
            sx={{ width: "100%" }}
            options={siteOptions}
            getOptionLabel={(option) => option.siteName}
            value={
              siteOptions.find(
                (option: any) => option.uid === selectedSharedUid
              ) || null
            }
            onChange={handleSelectWebsite}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search site name or choose from the list"
                label="Select Website"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
              />
            )}
            size="small"
          />
          <Button
            data-tracker="135"
            variant="contained"
            color="secondary"
            disabled={!selectedSharedUid}
            sx={{ whiteSpace: "nowrap" }}
            onClick={handleSearch}
          >
            SELECT SITE
          </Button>
        </Stack>
      </Paper>
      {dataShow && (
        <>
          {loading ? (
            <SimpleBackdrop />
          ) : dataLoaded ? (
            <>
              <ThemeTable tableHeadings={tableHeadings} />
            </>
          ) : null}
        </>
      )}
    </>
  );
}

export default Theme;
